import Phaser from 'phaser';
import type { Hole } from '../Playfield';
import { tokens } from '../../design/tokens';
import { hex } from '../../design/palette';
import { StarfieldHolesPostFX } from './StarfieldHolesPostFX';

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function hexToRgbVec3(s: string): [number, number, number] {
  const n = hex(s);
  return [
    ((n >> 16) & 0xff) / 255,
    ((n >>  8) & 0xff) / 255,
    ( n        & 0xff) / 255,
  ];
}

export interface BallWorldPos {
  x: number;
  y: number;
}

export class StarfieldHolesController {
  private filter: StarfieldHolesPostFX | null = null;
  private active = !prefersReducedMotion();

  constructor(
    private scene: Phaser.Scene,
    private getBallWorldPos: () => BallWorldPos,
  ) {
    if (!this.active) return;
    if (scene.sys.renderer.type !== Phaser.WEBGL) {
      this.active = false;
      return;
    }
    try {
      const cam = scene.cameras.main;
      this.filter = new StarfieldHolesPostFX(cam);
      // Attach BEFORE TrippyPostFX is added (caller controls construction order)
      // so trippy effects layer on top of the starfield.
      cam.filters.external.add(this.filter);
    } catch (e) {
      console.warn('StarfieldHolesPostFX failed to attach; starfield disabled', e);
      this.active = false;
      this.filter = null;
      return;
    }
    this.applyTokens();
  }

  private applyTokens(): void {
    if (!this.filter) return;
    const sf = tokens.holes.starfield;
    this.filter.uWakeRadiusPx       = sf.wakeRadiusPx;
    this.filter.uBaseStarBrightness = sf.baseStarBrightness;
    this.filter.uWakeStarBoost      = sf.wakeStarBoost;
    this.filter.uWakeRimShimmer     = sf.wakeRimShimmerStrength;
    this.filter.uTwinkleHz          = sf.twinkleHz;
    this.filter.uWarmFraction       = sf.warmFraction;
    this.filter.uStarColorCool      = hexToRgbVec3(sf.starColorCool);
    this.filter.uStarColorWarm      = hexToRgbVec3(sf.starColorWarm);
  }

  /**
   * Kept for API compatibility with GameScene.loadLevel callers. The shader
   * now uses a color-key on the underlying scene to determine which cells
   * are real holes vs. cleared cells / target, so no per-level state needs
   * to be uploaded.
   */
  public setHoles(_target: Hole, _clearCells: Set<string>): void {
    // No-op.
  }

  public tick(deltaMs: number): void {
    if (!this.active || !this.filter) return;
    this.filter.uTimeMs += deltaMs;
    this.filter.uCameraScrollY = this.scene.cameras.main.scrollY;
    const b = this.getBallWorldPos();
    this.filter.uBallPos = [b.x, b.y];
  }

  public isActive(): boolean {
    return this.active;
  }

  public destroy(): void {
    if (!this.filter) return;
    try {
      this.scene.cameras.main.filters.external.remove(this.filter);
    } catch {
      // Camera or filter list may already be torn down.
    }
    this.filter = null;
  }
}
